import styled from '@emotion/styled';

import { HardSkillIcon } from './icons/HardSkillIcon';
import { LanguageSkillIcon } from './icons/LanguageSkillIcon';
import { SoftSkillIcon } from './icons/SoftSkillIcon';

import type { SkillType } from '../../../shared/source/data-contract/skill';
import type { FunctionComponent, ReactNode } from 'react';

export type SkillTagProps = {
    readonly isActive?: boolean;
    readonly label: string;
    readonly onClick?: () => void;
    readonly type: SkillType;
};

const StyledSkillTag = styled.div<{ readonly isActive: boolean }>(({ isActive }) => ({
    '&:active': {
        transform: 'scale(0.95)',
    },
    alignItems: 'center',
    background: '#F1F1F0',
    borderRadius: '0.5rem',
    boxShadow: isActive
        ? '0rem 0rem 0.0625rem 0rem rgba(66, 66, 66, 0.00), 0rem 0rem 0.0625rem 0rem rgba(66, 66, 66, 0.02), 0rem 0rem 0.0625rem 0rem rgba(66, 66, 66, 0.07), 0rem 0rem 0.0625rem 0rem rgba(66, 66, 66, 0.11), -0.0156rem -0.0156rem 0rem 0rem rgba(0, 0, 0, 0.05), 0.0625rem 0.0625rem 0.0625rem 0rem rgba(251, 251, 255, 0.75) inset, -0.0313rem -0.0313rem 0.0625rem 0rem rgba(0, 0, 0, 0.07) inset'
        : '0.0625rem 0.0625rem 0.0625rem 0rem #FBFBFF inset, -0.0313rem -0.0313rem 0.0625rem 0rem rgba(0, 0, 0, 0.07) inset, 0.0138rem 0.0138rem 0.0194rem -0.0313rem rgba(0, 0, 0, 0.13), 0.0188rem 0.0188rem 0.0538rem -0.0469rem rgba(0, 0, 0, 0.13), 0.0831rem 0.0831rem 0.1175rem -0.0706rem rgba(0, 0, 0, 0.11), 0.1844rem 0.1844rem 0.2613rem -0.0938rem rgba(0, 0, 0, 0.10), 0.3125rem 0.3125rem 0.6625rem -0.1175rem rgba(0, 0, 0, 0.03), -0.02rem -0.02rem 0rem 0rem rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    display: 'inline-flex',
    gap: '0.375rem',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '0.375rem 0.625rem',
    transform: isActive ? 'scale(0.95) translateY(0.065rem)' : 'scale(1)',
    transition: 'transform 0.2s ease-in-out',
}));

const SkillIconWrapper = styled.div({
    alignItems: 'center',
    display: 'flex',
    height: '0.75rem',
    justifyContent: 'center',
    width: '0.75rem',
});

const SkillName = styled.span({
    color: '#353E45',
    fontFamily: 'FT Regola Neue',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.25rem',
});

export const SkillTag: FunctionComponent<SkillTagProps> = ({ isActive = false, label, onClick, type }) => {
    const renderIcon: () => ReactNode = () => {
        switch (type) {
            case 'hard':
                return <HardSkillIcon isActive={isActive} />;
            case 'soft':
                return <SoftSkillIcon isActive={isActive} />;
            case 'language':
                return <LanguageSkillIcon isActive={isActive} />;
            default:
                return null;
        }
    };

    return (
        <StyledSkillTag isActive={isActive} onClick={onClick}>
            <SkillIconWrapper>{renderIcon()}</SkillIconWrapper>
            <SkillName>{label}</SkillName>
        </StyledSkillTag>
    );
};
